/**
 * Coups de pouce — Attendu 663 « Construire un angle de mesure donnée ».
 *
 * Une entrée par `generator_key` (cf. gen_663.js). Une entrée peut être un objet
 * `{ pages }` ou une fonction `(question) => ({ pages })` (cf. utils/coupDePouce.js).
 *
 * Règle absolue : l'aide porte sur d'AUTRES valeurs que celles que le
 * générateur peut tirer, sinon elle donne la réponse.
 * Emphase : **texte** (rendu en gras, pas de HTML brut). Pas de tiret cadratin.
 */

// -- Figure Ex. 1 : les quatre deplacements sur un cadran --
// Ici la table EST la lecon : impossible de montrer la correspondance sans
// donner les quatre valeurs. Le format ne teste que ces quatre-la, et l'eleve
// doit encore relier son deplacement au bon cadran.
const SVG_TOURS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 178" role="img"
     aria-label="Quatre cadrans : un quart de tour vaut 90 degres, un demi-tour 180, trois quarts de tour 270, un tour complet 360.">
  <circle cx="45" cy="80" r="32" fill="none" stroke="#CBD5E1" stroke-width="1.2"/>
  <line x1="45" y1="80" x2="77" y2="80" stroke="#94A3B8" stroke-width="1.6"/>
  <line x1="45" y1="80" x2="45.0" y2="48.0" stroke="#0f172a" stroke-width="2.2"/>
  <path d="M 65 80 A 20 20 0 0 0 45.0 60.0" fill="none" stroke="#F97316" stroke-width="2"/>
  <polyline points="50.0,56.5 45.0,60.0 50.0,63.5" fill="none" stroke="#F97316" stroke-width="2" stroke-linecap="round"/>
  <circle cx="45" cy="80" r="2.6" fill="#0f172a"/>
  <text x="45" y="134" text-anchor="middle" font-size="10.5" font-weight="700" fill="#0f172a">un quart</text>
  <text x="45" y="147" text-anchor="middle" font-size="10.5" font-weight="700" fill="#0f172a">de tour</text>
  <text x="45" y="166" text-anchor="middle" font-size="12.5" font-weight="700" fill="#F97316">90&#176;</text>
  <circle cx="135" cy="80" r="32" fill="none" stroke="#CBD5E1" stroke-width="1.2"/>
  <line x1="135" y1="80" x2="167" y2="80" stroke="#94A3B8" stroke-width="1.6"/>
  <line x1="135" y1="80" x2="103.0" y2="80.0" stroke="#0f172a" stroke-width="2.2"/>
  <path d="M 155 80 A 20 20 0 0 0 115.0 80.0" fill="none" stroke="#F97316" stroke-width="2"/>
  <polyline points="111.5,75.0 115.0,80.0 118.5,75.0" fill="none" stroke="#F97316" stroke-width="2" stroke-linecap="round"/>
  <circle cx="135" cy="80" r="2.6" fill="#0f172a"/>
  <text x="135" y="134" text-anchor="middle" font-size="10.5" font-weight="700" fill="#0f172a">un demi-</text>
  <text x="135" y="147" text-anchor="middle" font-size="10.5" font-weight="700" fill="#0f172a">tour</text>
  <text x="135" y="166" text-anchor="middle" font-size="12.5" font-weight="700" fill="#F97316">180&#176;</text>
  <circle cx="225" cy="80" r="32" fill="none" stroke="#CBD5E1" stroke-width="1.2"/>
  <line x1="225" y1="80" x2="257" y2="80" stroke="#94A3B8" stroke-width="1.6"/>
  <line x1="225" y1="80" x2="225.0" y2="112.0" stroke="#0f172a" stroke-width="2.2"/>
  <path d="M 245 80 A 20 20 0 1 0 225.0 100.0" fill="none" stroke="#F97316" stroke-width="2"/>
  <polyline points="220.0,103.5 225.0,100.0 220.0,96.5" fill="none" stroke="#F97316" stroke-width="2" stroke-linecap="round"/>
  <circle cx="225" cy="80" r="2.6" fill="#0f172a"/>
  <text x="225" y="134" text-anchor="middle" font-size="10.5" font-weight="700" fill="#0f172a">trois quarts</text>
  <text x="225" y="147" text-anchor="middle" font-size="10.5" font-weight="700" fill="#0f172a">de tour</text>
  <text x="225" y="166" text-anchor="middle" font-size="12.5" font-weight="700" fill="#F97316">270&#176;</text>
  <circle cx="315" cy="80" r="32" fill="none" stroke="#CBD5E1" stroke-width="1.2"/>
  <line x1="315" y1="80" x2="347" y2="80" stroke="#94A3B8" stroke-width="1.6"/>
  <path d="M 335 80 A 20 20 0 1 0 295 80" fill="none" stroke="#F97316" stroke-width="2"/>
  <path d="M 295 80 A 20 20 0 0 0 334.6 79.6" fill="none" stroke="#F97316" stroke-width="2"/>
  <polyline points="340.0,76.5 335.0,80.0 340.0,83.5" fill="none" stroke="#F97316" stroke-width="2" stroke-linecap="round"/>
  <circle cx="315" cy="80" r="2.6" fill="#0f172a"/>
  <text x="315" y="134" text-anchor="middle" font-size="10.5" font-weight="700" fill="#0f172a">un tour</text>
  <text x="315" y="147" text-anchor="middle" font-size="10.5" font-weight="700" fill="#0f172a">complet</text>
  <text x="315" y="166" text-anchor="middle" font-size="12.5" font-weight="700" fill="#F97316">360&#176;</text>
</svg>`.trim();

// -- Figure Ex. 2 : l'état de la construction juste avant la dernière etape --
// La fiche ne liste PAS les cinq etapes, ce serait la reponse. Elle montre
// la contrainte qui impose leur ordre : le rapporteur est pose sur un cote
// deja trace, et le second cote (en pointille vert) reste a tracer.
// 65 deg : absent de MESURES_EX2.
const SVG_ETAPES = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 208" role="img"
     aria-label="Un rapporteur pose sur la demi-droite SA deja tracee, centre sur S, zero aligne. La graduation 65 degres est reperee, et le second cote SB est en pointille car il se trace en dernier.">
  <line x1="110" y1="150" x2="260" y2="150" stroke="#0f172a" stroke-width="2.4"/>
  <path d="M 210 150 A 100 100 0 0 0 10 150" fill="#EFF6FF" fill-opacity="0.7" stroke="#94A3B8" stroke-width="1.4"/>
  <line x1="10" y1="150" x2="210" y2="150" stroke="#94A3B8" stroke-width="1.4"/>
  <line x1="210.0" y1="150.0" x2="197.0" y2="150.0" stroke="#94A3B8" stroke-width="1.3"/>
  <text x="182.0" y="144.5" text-anchor="middle" font-size="9" fill="#64748b">0</text>
  <line x1="208.5" y1="132.6" x2="201.6" y2="133.9" stroke="#94A3B8" stroke-width="0.8"/>
  <line x1="204.0" y1="115.8" x2="197.4" y2="118.2" stroke="#94A3B8" stroke-width="0.8"/>
  <line x1="196.6" y1="100.0" x2="185.3" y2="106.5" stroke="#94A3B8" stroke-width="1.3"/>
  <text x="172.4" y="117.5" text-anchor="middle" font-size="9" fill="#64748b">30</text>
  <line x1="186.6" y1="85.7" x2="181.2" y2="90.2" stroke="#94A3B8" stroke-width="0.8"/>
  <line x1="174.3" y1="73.4" x2="169.8" y2="78.8" stroke="#94A3B8" stroke-width="0.8"/>
  <line x1="160.0" y1="63.4" x2="153.5" y2="74.7" stroke="#94A3B8" stroke-width="1.3"/>
  <line x1="144.2" y1="56.0" x2="141.8" y2="62.6" stroke="#94A3B8" stroke-width="0.8"/>
  <line x1="127.4" y1="51.5" x2="126.1" y2="58.4" stroke="#94A3B8" stroke-width="0.8"/>
  <line x1="110.0" y1="50.0" x2="110.0" y2="63.0" stroke="#94A3B8" stroke-width="1.3"/>
  <line x1="92.6" y1="51.5" x2="93.9" y2="58.4" stroke="#94A3B8" stroke-width="0.8"/>
  <line x1="75.8" y1="56.0" x2="78.2" y2="62.6" stroke="#94A3B8" stroke-width="0.8"/>
  <line x1="60.0" y1="63.4" x2="66.5" y2="74.7" stroke="#94A3B8" stroke-width="1.3"/>
  <text x="74.0" y="91.1" text-anchor="middle" font-size="9" fill="#64748b">120</text>
  <line x1="45.7" y1="73.4" x2="50.2" y2="78.8" stroke="#94A3B8" stroke-width="0.8"/>
  <line x1="33.4" y1="85.7" x2="38.8" y2="90.2" stroke="#94A3B8" stroke-width="0.8"/>
  <line x1="23.4" y1="100.0" x2="34.7" y2="106.5" stroke="#94A3B8" stroke-width="1.3"/>
  <text x="47.6" y="117.5" text-anchor="middle" font-size="9" fill="#64748b">150</text>
  <line x1="16.0" y1="115.8" x2="22.6" y2="118.2" stroke="#94A3B8" stroke-width="0.8"/>
  <line x1="11.5" y1="132.6" x2="18.4" y2="133.9" stroke="#94A3B8" stroke-width="0.8"/>
  <line x1="10.0" y1="150.0" x2="23.0" y2="150.0" stroke="#94A3B8" stroke-width="1.3"/>
  <text x="38.0" y="144.5" text-anchor="middle" font-size="9" fill="#64748b">180</text>
  <line x1="152.3" y1="59.4" x2="145.5" y2="73.9" stroke="#1D9E75" stroke-width="2.6"/>
  <text x="139.6" y="90.6" text-anchor="middle" font-size="11.5" font-weight="700" fill="#1D9E75">65&#176;</text>
  <line x1="110" y1="150" x2="169.2" y2="23.1" stroke="#1D9E75" stroke-width="2.2" stroke-dasharray="6 5"/>
  <circle cx="110" cy="150" r="3.4" fill="#0f172a"/>
  <text x="102" y="168" text-anchor="end" font-size="13" font-weight="700" fill="#0f172a">S</text>
  <text x="268" y="155" font-size="13" font-weight="700" fill="#0f172a">A</text>
  <text x="177.2" y="19.1" font-size="13" font-weight="700" fill="#1D9E75">B</text>
  <text x="180" y="184" text-anchor="middle" font-size="11.5" font-weight="700" fill="#0f172a">le rapporteur se pose sur un côté déjà tracé</text>
  <text x="180" y="200" text-anchor="middle" font-size="11.5" fill="#1D9E75">le second côté se trace en dernier</text>
</svg>`.trim();

// -- Figures Ex. 4 : les trois decompositions --
// 125, 115 et 90 : aucune de ces trois valeurs n'est dans le pool de son
// propre regime (MESURES_V1, MESURES_V2, MESURES_V3), la fiche ne peut donc
// pas coincider avec le tirage qu'elle accompagne.
const SVG_DROIT_PLUS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 160" role="img"
     aria-label="Un angle droit de 90 degres auquel on ajoute 35 degres pour obtenir 125 degres.">
  <line x1="110" y1="115" x2="205.0" y2="115.0" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="110" y1="115" x2="110.0" y2="20.0" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="110" y1="115" x2="55.5" y2="37.2" stroke="#0f172a" stroke-width="2.2"/>
  <polyline points="123,115 123,102 110,102" fill="none" stroke="#F97316" stroke-width="2"/>
  <path d="M 110.0 73.0 A 42 42 0 0 0 85.9 80.6" fill="none" stroke="#1D9E75" stroke-width="2.2"/>
  <text x="151.0" y="78.0" text-anchor="middle" font-size="11.5" font-weight="700" fill="#F97316">90&#176;</text>
  <text x="91.4" y="59.9" text-anchor="middle" font-size="11.5" font-weight="700" fill="#1D9E75">35&#176;</text>
  <circle cx="110" cy="115" r="3" fill="#0f172a"/>
  <text x="150" y="152" text-anchor="middle" font-size="12.5" font-weight="700" fill="#0f172a">90&#176; + 35&#176; = 125&#176;</text>
</svg>`.trim();

const SVG_PLAT_MOINS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 160" role="img"
     aria-label="Un angle plat de 180 degres duquel on retire 65 degres pour obtenir 115 degres.">
  <line x1="110" y1="115" x2="205.0" y2="115.0" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="110" y1="115" x2="15.0" y2="115.0" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="110" y1="115" x2="69.9" y2="28.9" stroke="#0f172a" stroke-width="2.2"/>
  <path d="M 148.0 115.0 A 38 38 0 0 0 93.9 80.6" fill="none" stroke="#1D9E75" stroke-width="2.2"/>
  <path d="M 85.5 62.4 A 58 58 0 0 0 52.0 115.0" fill="none" stroke="#E24B4A" stroke-width="2.2"/>
  <text x="137.9" y="75.1" text-anchor="middle" font-size="11.5" font-weight="700" fill="#1D9E75">115&#176;</text>
  <text x="49.3" y="80.3" text-anchor="middle" font-size="11.5" font-weight="700" fill="#E24B4A">65&#176;</text>
  <circle cx="110" cy="115" r="3" fill="#0f172a"/>
  <text x="150" y="152" text-anchor="middle" font-size="12.5" font-weight="700" fill="#0f172a">180&#176; − 65&#176; = 115&#176;</text>
</svg>`.trim();

const SVG_DEUX_EGAUX = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 160" role="img"
     aria-label="Deux angles aigus egaux et adjacents de 45 degres donnent un angle de 90 degres.">
  <line x1="110" y1="115" x2="205.0" y2="115.0" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="110" y1="115" x2="177.2" y2="47.8" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="110" y1="115" x2="110.0" y2="20.0" stroke="#0f172a" stroke-width="2.2"/>
  <path d="M 148.0 115.0 A 38 38 0 0 0 136.9 88.1" fill="none" stroke="#378ADD" stroke-width="2.2"/>
  <path d="M 151.0 74.0 A 58 58 0 0 0 110.0 57.0" fill="none" stroke="#9333EA" stroke-width="2.2"/>
  <text x="158.0" y="99.1" text-anchor="middle" font-size="11.5" font-weight="700" fill="#378ADD">45&#176;</text>
  <text x="137.6" y="52.5" text-anchor="middle" font-size="11.5" font-weight="700" fill="#9333EA">45&#176;</text>
  <circle cx="110" cy="115" r="3" fill="#0f172a"/>
  <text x="150" y="152" text-anchor="middle" font-size="12.5" font-weight="700" fill="#0f172a">45&#176; + 45&#176; = 90&#176;</text>
</svg>`.trim();

// -- Figure Ex. 5 : les deux zones de part et d'autre de l'angle droit --
// Aucune mesure du pool MESURES_EX6 (35, 52, 68, 95, 112, 128, 145) ne figure
// sur la fiche : seuls 0, 90 et 180 y sont ecrits, et aucun des trois ne peut
// etre tire. La fiche donne la frontiere, pas la reponse.
const SVG_ZONES = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 194" role="img"
     aria-label="Un demi-disque partage en deux zones par l angle droit : a droite les angles aigus sous 90 degres, a gauche les angles obtus entre 90 et 180 degres.">
  <path d="M 180 150 L 290.0 150.0 A 110 110 0 0 0 180.0 40.0 Z" fill="#378ADD" fill-opacity="0.13"/>
  <path d="M 180 150 L 180.0 40.0 A 110 110 0 0 0 70.0 150.0 Z" fill="#E24B4A" fill-opacity="0.13"/>
  <path d="M 290.0 150.0 A 110 110 0 0 0 70.0 150.0" fill="none" stroke="#94A3B8" stroke-width="1.4"/>
  <line x1="180" y1="150" x2="290.0" y2="150.0" stroke="#0f172a" stroke-width="2"/>
  <line x1="180" y1="150" x2="180.0" y2="40.0" stroke="#0f172a" stroke-width="2"/>
  <line x1="180" y1="150" x2="70.0" y2="150.0" stroke="#0f172a" stroke-width="2"/>
  <polyline points="193,150 193,137 180,137" fill="none" stroke="#F97316" stroke-width="1.8"/>
  <circle cx="180" cy="150" r="3.2" fill="#0f172a"/>
  <text x="300" y="155" font-size="12" font-weight="700" fill="#0f172a">0&#176;</text>
  <text x="180" y="32" text-anchor="middle" font-size="12" font-weight="700" fill="#F97316">90&#176;</text>
  <text x="60" y="155" text-anchor="end" font-size="12" font-weight="700" fill="#0f172a">180&#176;</text>
  <text x="228" y="100" text-anchor="middle" font-size="13" font-weight="700" fill="#378ADD">aigu</text>
  <text x="228" y="117" text-anchor="middle" font-size="11" fill="#334155">moins de 90&#176;</text>
  <text x="132" y="100" text-anchor="middle" font-size="13" font-weight="700" fill="#E24B4A">obtus</text>
  <text x="132" y="117" text-anchor="middle" font-size="11" fill="#334155">entre 90&#176; et 180&#176;</text>
  <text x="180" y="186" text-anchor="middle" font-size="11.5" font-weight="700" fill="#0f172a">quart de tour = 90&#176;   ·   demi-tour = 180&#176;</text>
</svg>`.trim();

const CP_663 = {
    deplacements_mesures_qcm: {
        pages: [
            { titre: 'La règle', svg: SVG_TOURS },
        ],
    },
    etapes_construction_assoc: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Le rapporteur ne peut se poser que sur une demi-droite **déjà tracée** : il faut donc un premier côté avant de le sortir.',
                    'Et on ne trace le **second côté** qu\'une fois la graduation repérée.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_ETAPES },
        ],
    },
    // La marche à suivre venait du second paragraphe de l'énoncé, retiré le
    // 2026-08-02 : elle a sa place ici, pas sous les yeux de l'élève qui sait
    // déjà faire. Même figure que l'exo 2, c'est le même geste.
    construction_angle_programme: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Place le **centre** du rapporteur sur le **sommet** de l\'angle.',
                    'Aligne sa **base** sur la demi-droite déjà tracée, le **0** posé sur ce côté.',
                    'Clique ensuite sur la **graduation** demandée : le second côté se trace jusqu\'à elle.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_ETAPES },
        ],
    },
    // ENTREE FONCTION : les trois regimes partent de reperes differents (angle
    // droit, angle plat, deux angles egaux). Une fiche unique n'aiderait aucun
    // des trois.
    // V1 : on complète un angle droit. V2 : on retire d'un angle plat.
    // V3 (défaut) : deux angles égaux et adjacents.
    decomposition_saisie: {
        variantes: [
            {
                sousType: 'V1',
                pages: [
                    { titre: 'La règle', lignes: [
                        'Un angle **droit** vaut **90°**. On lui ajoute un second angle pour atteindre la mesure voulue.',
                        'L\'angle à ajouter vaut donc la mesure visée **moins 90°**.',
                    ] },
                    { titre: 'Un exemple', svg: SVG_DROIT_PLUS },
                ],
            },
            {
                sousType: 'V2',
                pages: [
                    { titre: 'La règle', lignes: [
                        'Un angle **plat** vaut **180°**. On lui retire un angle pour redescendre à la mesure voulue.',
                        'L\'angle retiré vaut donc **180° moins** la mesure visée.',
                    ] },
                    { titre: 'Un exemple', svg: SVG_PLAT_MOINS },
                ],
            },
        ],
        defaut: {
            pages: [
                { titre: 'La règle', lignes: [
                    'Deux angles **égaux** et **adjacents** forment un angle deux fois plus grand.',
                    'Chacun vaut donc la **moitié** de la mesure visée.',
                ] },
                { titre: 'Un exemple', svg: SVG_DEUX_EGAUX },
            ],
        },
    },
    verification_qualitative_vf: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Un angle **aigu** mesure moins de **90°**. Un angle **obtus** est compris entre **90°** et **180°**.',
                    'Un **quart de tour** vaut 90° et un **demi-tour** vaut 180° : « moins qu\'un quart de tour » signifie donc moins de 90°.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_ZONES },
        ],
    },
};

export default CP_663;
